import { Check, Link2, X } from 'lucide-react'
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { apiErrorMessage } from '../../api/client'
import { guardianApi } from '../../api/endpoints'
import { ErrorMessage } from '../ErrorMessage'
import type { GuardianLinkViewResponse } from '../../types/api'

const RELATIONS = ['Mother', 'Father', 'Guardian', 'Other']

/** "Demo Guardian asked to be linked to you as your mother." / "Aarav asked you to link your account to theirs as their mother." */
function requestSentence(link: GuardianLinkViewResponse, iAmChild: boolean): string {
  const relation = link.relation ? link.relation.toLowerCase() : 'guardian'
  if (iAmChild) {
    return `${link.guardianName ?? 'Someone'} asked to be linked to you as your ${relation}.`
  }
  return `${link.childName} asked you to link your account to theirs as their ${relation}.`
}

export function GuardianLinks({
  participantId,
  guardianRequired,
}: {
  participantId: string
  /** False once the learner turns 18: no consent to collect, so nothing to ask for. */
  guardianRequired: boolean
}) {
  const [links, setLinks] = useState<GuardianLinkViewResponse[]>([])
  const [email, setEmail] = useState('')
  const [relation, setRelation] = useState(RELATIONS[0])
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(() => {
    guardianApi.listMyLinks().then(setLinks).catch(() => setLinks([]))
  }, [])

  useEffect(load, [load])

  const pending = links.filter((l) => l.awaitingMyApproval)
  const active = links.filter((l) => l.status === 'ACTIVE')
  const sent = links.filter((l) => l.status === 'PENDING' && !l.awaitingMyApproval)

  // An adult learner with no links at all has nothing to see here. Requests still waiting on them
  // and links they already hold stay visible whatever their age - those are decisions they made,
  // and a link they cannot see is a link they cannot reason about.
  const showLinkCard = guardianRequired || active.length > 0 || sent.length > 0
  if (pending.length === 0 && !showLinkCard) return null

  async function respond(link: GuardianLinkViewResponse, accept: boolean) {
    setError(null)
    try {
      await guardianApi.respond(link.id, { accept })
      load()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not answer this request'))
    }
  }

  async function invite(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setNotice(null)
    const trimmed = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Enter your parent or guardian's email address")
      return
    }
    setSubmitting(true)
    try {
      await guardianApi.inviteGuardian({ guardianEmail: trimmed, relation })
      setEmail('')
      setNotice(
        `Request sent to ${trimmed}. If they do not have a YellowDots account yet, the email has a link to create one.`,
      )
      load()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send the link request'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-4">
      {pending.length > 0 && (
        <div className="card-pad">
          <h2 className="flex items-center gap-2 font-bold text-ink-900">
            <Link2 size={16} className="text-ink-400" /> Link requests &middot; {pending.length}
          </h2>
          <div className="mt-3 space-y-3">
            {pending.map((link) => (
              <div key={link.id} className="rounded-xl border border-sun-400/50 bg-sun-400/10 p-3">
                <p className="text-sm font-semibold text-ink-900">
                  {requestSentence(link, link.childParticipantId === participantId)}
                </p>
                <p className="mt-1 text-xs text-amber-800">
                  Approve only if this is really your parent or guardian. They will approve every
                  connection you make and see your needs and activity.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => respond(link, true)} className="btn-sun !px-3 !py-1.5 text-xs">
                    <Check size={14} /> Approve
                  </button>
                  <button onClick={() => respond(link, false)} className="btn-ghost !px-3 !py-1.5 text-xs">
                    <X size={14} /> Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showLinkCard && (
      <div className="card-pad">
        <h2 className="font-bold text-ink-900">
          {guardianRequired ? 'Link a parent or guardian' : 'Parent or guardian'}
        </h2>
        {guardianRequired && (
          <p className="mt-0.5 text-xs text-ink-400">
            Learners under 18 send and accept connection requests only through a linked parent or guardian.
          </p>
        )}

        {active.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {active.map((link) => (
              <li key={link.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="pill bg-emerald-100 text-emerald-700">Linked</span>
                <span className="font-semibold text-ink-900">
                  {link.childParticipantId === participantId ? link.guardianName : link.childName}
                </span>
                <span className="text-ink-500">{link.relation}</span>
              </li>
            ))}
          </ul>
        )}

        {sent.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {sent.map((link) => (
              <li key={link.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="pill bg-ink-100 text-ink-600">Waiting</span>
                <span className="text-ink-600">
                  {(link.childParticipantId === participantId ? link.guardianName : link.childName) ?? 'The other person'} has
                  not answered yet
                </span>
              </li>
            ))}
          </ul>
        )}

        {!guardianRequired && (
          <p className="mt-2 text-xs text-ink-400">
            You are over 18, so a guardian is no longer required on your account.
          </p>
        )}

        {/* Once a parent has approved the link there is nothing left to ask for - the gate is met,
            and offering the form again reads as though it is not. */}
        {guardianRequired && active.length > 0 && (
          <p className="mt-3 text-sm text-emerald-700">
            Your parent or guardian is linked. Your connection requests go to them for approval.
          </p>
        )}

        {guardianRequired && active.length === 0 && (
        <>
        <p className="mt-3 text-sm text-ink-600">
          Ask your parent or guardian to link to your account. We email them the request - if they
          do not have a YellowDots account yet, the email includes a link to create one.
        </p>

        <form onSubmit={invite} className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input
            id="guardianEmail"
            name="guardian-email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input sm:flex-1"
            placeholder="Parent's email address"
          />
          <select
            value={relation}
            onChange={(e) => setRelation(e.target.value)}
            aria-label="Relation"
            className="input sm:w-40"
          >
            {RELATIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button type="submit" disabled={submitting} className="btn-sun shrink-0">
            {submitting ? 'Sending...' : 'Send link request'}
          </button>
        </form>

        {notice && <p className="mt-2 text-sm text-emerald-700">{notice}</p>}
        </>
        )}

        <div className="mt-2">
          <ErrorMessage message={error} />
        </div>
      </div>
      )}
    </div>
  )
}
